import { logMapInfo, logMapWarn } from './utils';
import { getEpisodeDiagnostics } from './episode-diagnostics';
import type { MapRelevantFlags } from './map-relevant-flags';
import type { MapSurface } from '../types';

/**
 * How long a WebGL failure may stay unrecovered before we consider the episode
 * a failure. A black map persisting this long is a real, user-visible outage
 * regardless of whether the SDK eventually claws the context back.
 */
export const DEFAULT_RECOVERY_TIMEOUT_MS = 15_000;

/** A signal that *might* mean the map recovered. Whether we trust it to actually
 * close an episode is governed by {@link TRUSTED_CLOSING_TRIGGERS}. */
export type RecoveryTrigger = 'context_restored' | 'fatal_error_cleared';

/**
 * Triggers we trust to actually mean "the map recovered." Empirically narrowed
 * via GPU-kill testing (2026-07):
 *   - `context_restored` fires only on genuine browser context restoration, and
 *     stays silent when the map does not recover — trusted.
 *   - `fatal_error_cleared` is a FALSE-POSITIVE source: `view.fatalError` can
 *     transiently drop to null mid-recovery-attempt and immediately re-set while
 *     the map is still black. Logged for visibility, never closes an episode.
 *
 * (`view.readyState` was also evaluated as a trigger but reads `undefined` in
 * this SDK version — it never fired — so it was dropped entirely.)
 */
const TRUSTED_CLOSING_TRIGGERS = new Set<RecoveryTrigger>(['context_restored']);

type FailureReason = 'timeout' | 'teardown';

type EpisodeState = 'ok' | 'broken' | 'failed';

type MapRecoveryTrackerOptions = {
    /** Which map this tracker watches. Stamped on every event as `mapSurface`. */
    surface: MapSurface;
    /**
     * The map-relevant flags, stamped on every emitted event so recovery rate can be
     * segmented by flag directly on the episode — even for departments without
     * diagnostic-context collection.
     */
    enabledFeatures?: MapRelevantFlags;
    /** When this tracker's own view was created. Map age is measured from here. */
    createdAtMs?: number;
    /** Reads the live `view.fatalError?.name` at event time (for diagnostics). */
    getFatalErrorName?: () => string | undefined;
    /**
     * Reads the live `view.suspended` at event time. We already count suspend/resume
     * cycles for the whole session; this is the instantaneous value, which is what
     * tells you whether the map was suspended *at the moment it failed*.
     */
    getViewSuspended?: () => boolean | undefined;
    /** Reads the map's container element at event time, for its rendered size. */
    getContainer?: () => HTMLElement | null;
    /** Override the failure timeout (defaults to {@link DEFAULT_RECOVERY_TIMEOUT_MS}). */
    timeoutMs?: number;
};

export type MapRecoveryTracker = {
    /** Call on every `webgl-context-lost` fatal error — alongside existing logging. */
    onFailure: () => void;
    /** Call when any recovery signal fires (context restored / fatalError cleared / readyState ready). */
    onRecoverySignal: (recoveredVia: RecoveryTrigger) => void;
    /** Call on map teardown/unmount — emits `recovery_failed` if an episode is still open. */
    dispose: () => void;
};

/**
 * Collapses the noisy raw WebGL failure/recovery stream into discrete "episodes"
 * and emits one outcome event per episode:
 *   - `recovery_succeeded` when an open episode is closed by a recovery signal
 *   - `recovery_failed`    when an open episode times out or the map tears down
 *
 * All events log under source `'tracker'` so they are easy to isolate from the
 * raw `webgl` / `sdk` map logs.
 *
 * This is ADDITIVE. It does not replace or suppress any existing map logging
 * (`context_lost`, `fatal_error`, `context_restored`, `ready_state_change`) — it
 * only reads those same signals to derive an episode-level recovery rate that is
 * immune to two problems that make raw counts misleading:
 *   1. `fatal_error` re-emit spam (a single failing recovery loops the watcher) —
 *      absorbed into one episode.
 *   2. "recovered once, then failed later" within a session — counted as two
 *      separate episodes, one success and one failure, instead of a single
 *      session-level boolean that hides the later failure.
 *
 * Per-map-instance state machine:
 *   ok     --failure-->                    broken   (open episode, start timeout)
 *   broken --failure-->                    broken   (absorb re-emit spam; same episode)
 *   broken --trusted recovery-->           ok       (close episode: recovery_succeeded)
 *   broken --timeout/teardown-->           failed   (close episode: recovery_failed)
 *   failed --failure-->                    failed   (absorb; do NOT re-report or reopen)
 *   failed --trusted recovery-->           ok       (recovered after we declared failure; reset)
 *   ok     --trusted recovery-->           ok       (spurious/late restore — logged, not counted)
 *
 * "trusted recovery" = a recovery signal in {@link TRUSTED_CLOSING_TRIGGERS}.
 * Untrusted signals are logged as `recovery_signal` but never change state.
 *
 * Note: a recovery arriving AFTER the timeout is counted as a failure, not a
 * late success — "recovered within {timeoutMs}" is the success definition, kept
 * intentionally simple and tunable.
 */
export function createMapRecoveryTracker({
    surface,
    enabledFeatures,
    getFatalErrorName,
    getViewSuspended,
    getContainer,
    createdAtMs,
    timeoutMs = DEFAULT_RECOVERY_TIMEOUT_MS,
}: MapRecoveryTrackerOptions): MapRecoveryTracker {
    let state: EpisodeState = 'ok';
    let episodeStartTs = 0;
    let attemptCount = 0;
    let failureTimer: ReturnType<typeof setTimeout> | undefined;

    const clearFailureTimer = () => {
        if (failureTimer !== undefined) {
            clearTimeout(failureTimer);
            failureTimer = undefined;
        }
    };

    // Live SDK fatalError plus the cheap renderer diagnostics (map age / failure
    // timing, heap, live WebGL context count, SDK error counters) captured on every
    // event, so each episode carries its own context and confounds instead of
    // needing a session-level log join after the fact.
    const snapshot = () => ({
        fatalError: getFatalErrorName?.() ?? null,
        viewSuspendedAtFailure: getViewSuspended?.(),
        ...getEpisodeDiagnostics({ container: getContainer?.(), mapCreatedAtMs: createdAtMs }),
    });

    const featureFlags = enabledFeatures ? { enabledFeatures: { ...enabledFeatures } } : {};

    const emitFailure = (reason: FailureReason) => {
        clearFailureTimer();
        logMapWarn({
            source: 'tracker',
            event: 'recovery_failed',
            surface,
            extraData: {
                reason,
                elapsedMs: Date.now() - episodeStartTs,
                attemptCount,
                ...snapshot(),
                ...featureFlags,
            },
        });
    };

    return {
        onFailure() {
            if (state === 'ok') {
                state = 'broken';
                episodeStartTs = Date.now();
                attemptCount = 1;
                clearFailureTimer();
                failureTimer = setTimeout(() => {
                    if (state === 'broken') {
                        state = 'failed';
                        emitFailure('timeout');
                    }
                }, timeoutMs);
                // Log entry into the broken state (a new episode opened). May be
                // redundant with the raw `fatal_error` log — that's fine.
                logMapWarn({
                    source: 'tracker',
                    event: 'episode_broken',
                    surface,
                    extraData: { ...snapshot(), ...featureFlags },
                });
                return;
            }
            // 'broken' or 'failed' — absorb the re-emit into the current episode.
            attemptCount++;
        },

        onRecoverySignal(recoveredVia: RecoveryTrigger) {
            // Observability: record EVERY recovery signal that fires, regardless
            // of episode state, so we retain visibility into which signals shift
            // the map back toward good — not just the trusted one that closes the
            // episode. `episodeState` marks whether this signal was the closer
            // ('broken'), a post-failure recovery ('failed'), or redundant/late
            // ('ok'). Kept as permanent telemetry: it's our early warning if an
            // untrusted signal (e.g. fatal_error_cleared) ever starts correlating
            // with real recoveries and the trust set needs revisiting.
            logMapInfo({
                source: 'tracker',
                event: 'recovery_signal',
                surface,
                extraData: {
                    signal: recoveredVia,
                    episodeState: state,
                    ...snapshot(),
                    ...featureFlags,
                },
            });

            // Only a TRUSTED trigger may close an episode. Untrusted triggers
            // (currently just fatal_error_cleared) are logged above for
            // observability but must never change episode state — they produce
            // false positives while the map is still black.
            if (!TRUSTED_CLOSING_TRIGGERS.has(recoveredVia)) {
                return;
            }

            if (state === 'broken') {
                clearFailureTimer();
                logMapInfo({
                    source: 'tracker',
                    event: 'recovery_succeeded',
                    surface,
                    extraData: {
                        recoveredVia,
                        durationMs: Date.now() - episodeStartTs,
                        attemptCount,
                        ...snapshot(),
                        ...featureFlags,
                    },
                });
                state = 'ok';
            } else if (state === 'failed') {
                // Context came back after we already reported the failure — reset
                // so a genuinely new future loss starts a fresh episode.
                state = 'ok';
            }
            // state === 'ok' → spurious/late restore. Already logged above.
        },

        dispose() {
            if (state === 'broken') {
                emitFailure('teardown');
            }
            clearFailureTimer();
            state = 'ok';
        },
    };
}
